import type { KitchenCalendar, Terms } from './types';

/*
 * The weekdays a plan delivers on, as ISO weekdays: 1 = Monday ... 7 = Sunday.
 * Any combination works: Mon/Tue/Wed only, Sundays only, every day.
 * A plan without its own days follows the kitchen's delivery days.
 */

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
export const WEEKDAY_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const;

/** Unique, sorted weekdays 1-7; nothing (or nothing valid) is `undefined`, meaning "the kitchen's days". */
export function normalizeDays(days: readonly number[] | null | undefined): number[] | undefined {
  if (!days) return undefined;
  const out = [...new Set(days.filter((d) => Number.isInteger(d) && d >= 1 && d <= 7))].sort((a, b) => a - b);
  return out.length ? out : undefined;
}

/** "1,2,3" (as stored) to [1, 2, 3]; empty or missing is `undefined`. */
export function parseDays(text: string | null | undefined): number[] | undefined {
  return text ? normalizeDays(text.split(',').map(Number)) : undefined;
}

/** [1, 2, 3] to "1,2,3" for storage; no days is null. */
export function daysToText(days: readonly number[] | null | undefined): string | null {
  return normalizeDays(days)?.join(',') ?? null;
}

/** Same set of weekdays (order and repeats ignored). */
export function sameDays(a: readonly number[] | null | undefined, b: readonly number[] | null | undefined): boolean {
  return daysToText(a) === daysToText(b);
}

/**
 * Short words for a set of weekdays: "Every day", "Mon–Sat", "Mon–Fri", "Sat & Sun", "Sundays",
 * "Mon, Tue, Wed". Runs of four or more days are written as a range ("Mon–Thu, Sat").
 */
export function daysLabel(days: readonly number[] | null | undefined): string {
  const d = normalizeDays(days);
  if (!d) return '';
  if (d.length === 7) return 'Every day';
  if (d.length === 1) return `${WEEKDAY_LONG[d[0]! - 1]}s`;
  if (d.length === 2 && d[0] === 6 && d[1] === 7) return 'Sat & Sun';
  const runs: Array<[number, number]> = [];
  for (const x of d) {
    const last = runs[runs.length - 1];
    if (last && x === last[1] + 1) last[1] = x;
    else runs.push([x, x]);
  }
  const name = (x: number) => WEEKDAY_SHORT[x - 1]!;
  return runs
    .flatMap(([a, b]) => (b - a >= 3 ? [`${name(a)}–${name(b)}`] : Array.from({ length: b - a + 1 }, (_, i) => name(a + i))))
    .join(', ');
}

/** The calendar one plan follows: its own weekdays when it has them, else the kitchen's. Holidays and meals stay the kitchen's. */
export function planCalendar(terms: Pick<Terms, 'days'>, cal: KitchenCalendar): KitchenCalendar {
  const own = normalizeDays(terms.days);
  return own ? { ...cal, deliveryDays: own } : cal;
}
